<template>
  <ViewWrapper class="onboarding" footer-orientation="vertical">
    <h2
      ref="onboardingHeadline"
      class="onboarding__headline"
      :class="{ 'onboarding__headline--centered': !hasRecommendedFeatures }"
    >
      {{ welcome }}
    </h2>
    <transition name="fade" mode="out-in">
      <i18n
        v-if="hasRecommendedFeatures"
        tag="p"
        path="view.onboarding.recommendedFeatures.label"
        class="onboarding__subline"
      >
        <template #recommendation>
          <i18n :path="recommendationI18nId">
            <template #fullscreen>
              <b>{{ features.fullscreen }}</b>
            </template>
            <template #sound>
              <b>{{ features.sound }}</b>
            </template>
          </i18n>
        </template>
      </i18n>
    </transition>

    <template #footer>
      <ButtonPill
        v-if="!supportsFullscreen || isFullscreen"
        class="onboarding__start"
        @click="startExperience"
      >
        {{ $t('view.onboarding.startExperience') }}
      </ButtonPill>

      <template v-else>
        <ButtonPill
          class="onboarding__start-fullscreen"
          icon="fullscreen"
          @click="startInFullscreen"
          >{{ $t('view.onboarding.enterFullscreenMode') }}</ButtonPill
        >
        <ButtonBase class="onboarding__start-without-fullscreen" @click="startExperience">
          {{ $t('view.onboarding.skipFullscreen') }}
        </ButtonBase>
      </template>

      <LegalFooter class="onboarding__legal-footer" />
    </template>
  </ViewWrapper>
</template>

<script>
import screenfull from 'screenfull';
import { gsap } from 'gsap/all';

import ButtonPill from '../components/ButtonPill';
import ButtonBase from '../components/ButtonBase';
import ViewWrapper from '../components/ViewWrapper';
import LegalFooter from '../components/LegalFooter';

import i18n from '../i18n';
import { soundController, setStartPlayback } from '../services/sound';

export default {
  name: 'Onboarding',

  components: {
    ButtonPill,
    ButtonBase,
    LegalFooter,
    ViewWrapper,
  },

  data() {
    return {
      supportsFullscreen: screenfull.isEnabled,
      isFullscreen: screenfull.isFullscreen,
      welcome: i18n.t('view.onboarding.welcome'),
    };
  },

  computed: {
    soundEnabled: () => soundController.soundEnabled,

    features() {
      return {
        fullscreen: this.$t('view.onboarding.recommendedFeatures.features.fullscreen'),
        sound: this.$t('view.onboarding.recommendedFeatures.features.sound'),
      };
    },

    hasRecommendedFeatures() {
      return !!this.recommendationI18nId;
    },

    recommendationI18nId() {
      // no label needed here
      if (!this.supportsFullscreen && this.soundEnabled) return;

      let recommendation;
      if (this.supportsFullscreen && !this.isFullscreen) {
        recommendation = this.soundEnabled
          ? 'view.onboarding.recommendedFeatures.recommendations.fullscreen'
          : 'view.onboarding.recommendedFeatures.recommendations.soundAndFullscreen';
      } else {
        recommendation = 'view.onboarding.recommendedFeatures.recommendations.sound';
      }
      return recommendation;
    },

    welcomeTexts() {
      // TODO: Automatically add all translations of all available languages
      const texts = [
        'Welcome',
        'Herzlich willkommen',
        '美好的一天',
        'Bienvenue',
        'Servus',
        'добро пожаловать',
        'مرحبا',
        'Bienvenido',
        'Bem-vindo',
      ];
      return texts;
    },
  },

  created() {
    screenfull.on('change', () => {
      this.isFullscreen = screenfull.isFullscreen;
    });
  },

  mounted() {
    this.showWelcomeAnimation();
  },

  methods: {
    startInFullscreen() {
      if (this.supportsFullscreen) {
        screenfull.request();
      }
      this.startExperience();
    },

    showWelcomeAnimation() {
      const vm = this;

      let i = 0;
      gsap.set(this.$refs.onboardingHeadline, { opacity: 0 });
      gsap.to(this.$refs.onboardingHeadline, {
        duration: 1,
        opacity: 1,
        repeat: -1,
        ease: 'Quad.easeInOut',
        yoyoEase: 'Quad.easeInOut',
        onRepeat() {
          i++;
          if (i % 2) return;
          const currentIndex = vm.welcomeTexts.indexOf(vm.welcome);
          const nextIndex = (currentIndex + 1) % vm.welcomeTexts.length;
          vm.welcome = vm.welcomeTexts[nextIndex];
        },
      });
    },

    startExperience() {
      setStartPlayback(true);
      this.$router.replace({ name: 'home' });
    },
  },
};
</script>

<style lang="scss" scoped>
.onboarding__headline {
  @include font-headline;
  font-weight: bold;
  padding-bottom: 12px;
  text-align: center;
  min-height: rem(2 * 37px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.onboarding__headline--centered {
  align-items: center;
}

.onboarding__subline {
  text-align: center;
  padding-left: 20px;
  padding-right: 20px;
  margin-bottom: 7vh;
  max-width: 260px;
  margin-left: auto;
  margin-right: auto;
}

.onboarding__start {
  margin-bottom: 10px;

  @include helper__greater($bp-425) {
    margin-bottom: 30px;
  }
}

.onboarding__start-without-fullscreen {
  font-size: rem(12px);
  text-decoration: underline;
  padding: 15px 30px;

  @include helper__greater($bp-425) {
    margin-bottom: 30px;
  }
}

.onboarding__legal-footer {
  width: 100%;
  margin-bottom: env(safe-area-inset-bottom);
}
</style>
